import { matchPath } from 'react-router-dom';

import { PARAMS } from 'common/constants';
import { STORY_CARD_EDITOR_PAGE, STORY_FLOW_PAGE } from 'admin/constants/routes';
import type { StoryModel } from 'types/story';

import type { EditorSurface, ToolCallDeps } from './types';

/**
 * Resolves the editor surface (i.e. where the Story Editor is loaded) and card ID
 * from the pathname.
 *
 * Important: the surface must be derived from the URL, not from Redux — the
 * `storyEditor` Redux state is also populated on the Versions and Embed surfaces,
 * so it can't be used to distinguish them.
 *
 * @param pathname - The pathname to resolve the editor surface and card ID from.
 * @returns The editor surface and card ID.
 */
export function resolveEditorSurface(pathname: string): {
	editorSurface: EditorSurface;
	cardId: string | null;
} {
	const cardMatch = matchPath<{ [PARAMS.CARD_ID]: string }>(pathname, {
		path: STORY_CARD_EDITOR_PAGE,
		exact: true,
	});
	if (cardMatch) {
		return {
			editorSurface: 'card',
			cardId: cardMatch.params[PARAMS.CARD_ID] ?? null,
		};
	}

	if (matchPath(pathname, { path: STORY_FLOW_PAGE, exact: true })) {
		return { editorSurface: 'flow', cardId: null };
	}

	return { editorSurface: 'unsupported', cardId: null };
}

export type EditableStoryReady = { ok: true; story: StoryModel } | { ok: false; error: string };

export function getEditableStoryReady(
	deps: Pick<ToolCallDeps, 'getEditorSurface' | 'getStory' | 'getVersion'>,
	toolName: string
): EditableStoryReady {
	if (deps.getEditorSurface() === 'unsupported') {
		return { ok: false, error: `${toolName} is only available on Story Flow or Card Editor.` };
	}

	const story = deps.getStory();
	if (!story) return { ok: false, error: 'No editable story is loaded.' };
	if (deps.getVersion() !== 'latest') {
		return { ok: false, error: `${toolName} only runs on the latest version.` };
	}
	return { ok: true, story };
}
